import { DocumentType, PageType } from '@innedit/innedit-type';
import { DataProps } from 'packages/formidable';
import { PageData } from 'packages/innedit';
import React, { FC, SyntheticEvent, useEffect, useState } from 'react';

import HOCGroup from '~/components/Group/HOC';

import Item from './Item';

// const sortFunc = (a, b) => b.datetime - a.datetime;

export interface FieldProps {
  label: string;
  name: string;
  type: string;
}

export interface DataPagesListProps extends Omit<DataProps, 'componentType'> {
  channelId: string;
  description?: string;
  display?: 'inside' | 'group';
  displayAdd?: boolean;
  editPathname?: string;
  espaceId: string;
  nothingLabel?: string;
  title?: string;
  type?: 'collection' | 'parent' | 'sub';
}

const DataPagesList: FC<DataPagesListProps> = ({
  channelId,
  description,
  display,
  displayAdd = true,
  editPathname,
  espaceId,
  nothingLabel,
  params,
  title,
  type = 'collection',
}) => {
  const [docs, setDocs] = useState<DocumentType<PageType>[]>();

  useEffect(() => {
    let isMounted = true;
    let unsub: (() => void) | undefined;

    const featureData = new PageData({
      channelId,
      espaceId,
      parentCollectionName: 'sub' === type ? params?.collectionName : undefined,
      parentId: 'sub' === type ? params?.docId : undefined,
    });

    if ('parent' !== type || params?.docId) {
      unsub = featureData.watch(
        newDocs => {
          if (isMounted) {
            setDocs(newDocs);
          }
        },
        {
          wheres: {
            parent: 'parent' === type ? params?.docId : undefined,
          },
        },
      );
    } else {
      setDocs([]);
    }

    return () => {
      isMounted = false;
      if (unsub) {
        unsub();
      }
    };
  }, [espaceId, params]);

  const handleAddOnClick = (event: SyntheticEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (['parent', 'sub'].includes(type) && !params?.docId) {
      throw new Error(
        'le docId est obligatoire pour associer cette feature a un parent',
      );
    }

    const model = new PageData({
      channelId,
      espaceId,
      parentCollectionName: 'sub' === type ? params?.collectionName : undefined,
      parentId: 'sub' === type ? params?.docId : undefined,
    });

    const newDoc: Partial<PageType> = model.initialize({
      parent: 'parent' === type ? params?.docId : '',
    });

    model.create({ ...newDoc, label: '<nouvelle page>' }).catch(error => {
      throw new Error(`Features List : handleAdd() : ${error.message}`);
    });
  };

  const handleRemoveOnClick = (event: SyntheticEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const index = event.currentTarget.getAttribute('data-index');
    if (docs && index) {
      const featureData = new PageData({
        channelId,
        espaceId,
        parentCollectionName:
          !espaceId || 'sub' === type ? params?.collectionName : undefined,
        parentId: !espaceId || 'sub' === type ? params?.docId : undefined,
      });

      featureData.delete(docs[parseInt(index, 10)].id);
    }
  };

  if ('parent' === type && (!params || !params.docId)) {
    return null;
  }

  return (
    <HOCGroup
      addOnClick={displayAdd ? handleAddOnClick : undefined}
      description={description}
      display={display}
      title={title}
    >
      {!docs && <p className="m-0">Chargement en cours</p>}
      {docs && 0 === docs.length ? (
        <p className="m-0">{nothingLabel || 'Aucune page'}</p>
      ) : (
        <div className="flex flex-col space-y-2">
          {docs?.map((doc, index) => (
            <Item
              key={doc.id || index}
              doc={doc}
              editPathname={editPathname}
              index={index}
              removeOnClick={handleRemoveOnClick}
            />
          ))}
        </div>
      )}
    </HOCGroup>
  );
};

export default DataPagesList;
